// cs.chat/1 wire format (JSON). Same compatibility rules as the control channel: unknown types
// and fields are ignored; new behavior gets a new `t`.

export const MAX_TEXT_LENGTH = 4_000;
/** Messages sent in a history reply (and kept for late joiners). */
export const HISTORY_LIMIT = 500;

export interface ChatMessage {
  /** Random id; duplicates (from several peers' histories) are merged by it. */
  id: string;
  clientId: string;
  /** Author's name and color at send time, so history stays readable after they leave. */
  name: string;
  color: string;
  /** Sender's wall clock (display only). */
  ts: number;
  /** Lamport clock: orders messages consistently on every peer. */
  lamport: number;
  text: string;
}

/**
 * One person's reaction to a message with one emoji. Toggling sends a new record; the highest
 * lamport wins (ties: "on" wins), so every peer converges on the same set.
 */
export interface Reaction {
  msgId: string;
  clientId: string;
  /** Who reacted (shown on hover), as of the reaction. */
  name: string;
  emoji: string;
  on: boolean;
  lamport: number;
}

export const MAX_EMOJI_LENGTH = 16;

export type ChatWire =
  | { t: "msg"; msg: ChatMessage }
  | { t: "react"; reaction: Reaction }
  | { t: "typing"; clientId: string; name: string; on: boolean }
  /** Ask for history. `since` (a lamport) is reserved for incremental sync; 0 = everything. */
  | { t: "history.req"; since: number }
  /** `reactions` (added later) is optional: older peers send and ignore it. */
  | { t: "history.res"; msgs: ChatMessage[]; reactions?: Reaction[] };

type Json = Record<string, unknown>;
const isObject = (v: unknown): v is Json =>
  typeof v === "object" && v !== null && !Array.isArray(v);
const isString = (v: unknown): v is string => typeof v === "string";
const isNumber = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);

export function decodeMessage(value: unknown): ChatMessage | undefined {
  if (!isObject(value)) return undefined;
  const { id, clientId, name, color, ts, lamport, text } = value;
  if (!isString(id) || !isString(clientId) || !isString(name) || !isString(color)) return undefined;
  if (!isNumber(ts) || !isNumber(lamport) || !isString(text)) return undefined;
  if (id.length > 100 || name.length > 100 || color.length > 32) return undefined;
  return { id, clientId, name, color, ts, lamport, text: text.slice(0, MAX_TEXT_LENGTH) };
}

export function decodeReaction(value: unknown): Reaction | undefined {
  if (!isObject(value)) return undefined;
  const { msgId, clientId, name, emoji, on, lamport } = value;
  if (!isString(msgId) || !isString(clientId) || !isString(name) || !isString(emoji))
    return undefined;
  if (typeof on !== "boolean" || !isNumber(lamport)) return undefined;
  if (msgId.length > 100 || clientId.length > 100 || !emoji || emoji.length > MAX_EMOJI_LENGTH)
    return undefined;
  return { msgId, clientId, name: name.slice(0, 100), emoji, on, lamport };
}

/** Which of two records for the same (message, person, emoji) wins. */
export function newerReaction(a: Reaction, b: Reaction): Reaction {
  if (a.lamport !== b.lamport) return a.lamport > b.lamport ? a : b;
  return a.on ? a : b;
}

/** Parses one wire message; anything unknown or malformed is undefined (ignored). */
export function decodeWire(raw: string): ChatWire | undefined {
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return undefined;
  }
  if (!isObject(value) || !isString(value.t)) return undefined;
  switch (value.t) {
    case "msg": {
      const msg = decodeMessage(value.msg);
      return msg ? { t: "msg", msg } : undefined;
    }
    case "typing":
      return isString(value.clientId) && isString(value.name) && typeof value.on === "boolean"
        ? { t: "typing", clientId: value.clientId, name: value.name.slice(0, 100), on: value.on }
        : undefined;
    case "react": {
      const reaction = decodeReaction(value.reaction);
      return reaction ? { t: "react", reaction } : undefined;
    }
    case "history.req":
      return { t: "history.req", since: isNumber(value.since) ? value.since : 0 };
    case "history.res": {
      if (!Array.isArray(value.msgs)) return undefined;
      const msgs = value.msgs
        .slice(0, HISTORY_LIMIT)
        .map(decodeMessage)
        .filter((m): m is ChatMessage => m !== undefined);
      if (!Array.isArray(value.reactions)) return { t: "history.res", msgs };
      const reactions = value.reactions
        .slice(0, HISTORY_LIMIT * 20)
        .map(decodeReaction)
        .filter((r): r is Reaction => r !== undefined);
      return { t: "history.res", msgs, reactions };
    }
    default:
      return undefined;
  }
}

/** Total order shared by every peer: lamport, then client id, then id. */
export function compareMessages(a: ChatMessage, b: ChatMessage): number {
  return a.lamport - b.lamport || a.clientId.localeCompare(b.clientId) || a.id.localeCompare(b.id);
}
